import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import {
    Collapse,
    Container,
    Divider,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { getNavBar, getPathsProducts, getPathsProductTypes } from '../../services/ContentServices'
import { Component, ModuleComponents, pathsProducts } from '../../types/ContentStructure'
import ComponentType from '../component_type/ComponentType'
import { getComponent, slugify } from '../../utils/utils'

// ─── Tipos ───────────────────────────────────────────────────────────────────
interface NavProduct {
    slug: string       // slugify(name) → ruta
    displayName: string
    href: string       // /{typeSlug}/{productSlug}
}

interface NavGroup {
    label: string       // Título que configuró el admin en CMS
    href: string | null // null si el tipo no tiene página propia
    typeSlug: string
    hasTypePage: boolean
    products: NavProduct[]
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
/** Convierte un slug "credito-personal" a "Crédito personal" */
function prettySlug(s: string) {
    return s.replace(/-/g, ' ').replace(/^./, c => c.toUpperCase())
}

export default function NavBar() {
    const navigate = useNavigate()
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [navBar, setNavBar] = useState<Component>()
    const [navGroups, setNavGroups] = useState<NavGroup[]>([])
    const [openMenuIdx, setOpenMenuIdx] = useState<number | null>(null)
    const [mobileExpanded, setMobileExpanded] = useState<number | null>(null)
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    // ── Carga de datos ───────────────────────────────────────────────────────
    useEffect(() => {
        const fetchAll = async () => {
            const [navRes, productsRes, typesRes] = await Promise.all([
                getNavBar(),
                getPathsProducts(),
                getPathsProductTypes(),
            ])

            // NavBar CMS
            const data = navRes.data as ModuleComponents
            const root = data.components[0].component
            setNavBar(root)

            // Slugs de tipos que tienen página propia
            const typesWithPage: string[] = (typesRes.data as { name: string }[])
                .map((t: { name: string }) => slugify(t.name))

            // Productos agrupados por tipo
            const allProducts: (pathsProducts & { displayName?: string })[] = productsRes.data

            const byType: Record<string, NavProduct[]> = {}
            allProducts.forEach((p) => {
                const typeSlug = slugify(p.productTypeName)
                const productSlug = slugify(p.name)
                if (!byType[typeSlug]) byType[typeSlug] = []
                byType[typeSlug].push({
                    slug: productSlug,
                    displayName: (p as any).displayName?.trim() || prettySlug(productSlug),
                    href: `/${typeSlug}/${productSlug}`,
                })
            })

            // Extraer LINKs del CMS (sección "Navegación")
            const cmsLinks: Component[] = []
            getComponent('SECCION', root.components)?.forEach((sec: Component) => {
                getComponent('LINK', sec.components)?.forEach((lnk: Component) => {
                    cmsLinks.push(lnk)
                })
            })

            // Construir NavGroups combinando CMS + rutas automáticas
            const groups: NavGroup[] = cmsLinks.map((link) => {
                const rawUrl = link.link || ''
                const firstSeg = rawUrl.split('/').filter(Boolean)[0] || ''
                const hasPage  = typesWithPage.includes(firstSeg)
                const prods    = byType[firstSeg] ?? []

                return {
                    label:       link.title || prettySlug(firstSeg),
                    href:        hasPage ? `/${firstSeg}` : null,
                    typeSlug:    firstSeg,
                    hasTypePage: hasPage,
                    products:    prods,
                }
            })

            // Añadir tipos que tienen productos pero ningún CMS link los cubre
            const coveredSlugs = new Set(groups.map(g => g.typeSlug))
            Object.entries(byType).forEach(([typeSlug, prods]) => {
                if (!coveredSlugs.has(typeSlug) && prods.length > 0) {
                    groups.push({
                        label:       prettySlug(typeSlug),
                        href:        typesWithPage.includes(typeSlug) ? `/${typeSlug}` : null,
                        typeSlug,
                        hasTypePage: typesWithPage.includes(typeSlug),
                        products:    prods,
                    })
                }
            })

            setNavGroups(groups)
        }
        fetchAll()
    }, [])

    // ── Timers hover (evita cierre brusco al pasar entre item y dropdown) ────
    const handleMouseEnter = (idx: number) => {
        if (closeTimer.current) clearTimeout(closeTimer.current)
        setOpenMenuIdx(idx)
    }
    const handleMouseLeave = () => {
        closeTimer.current = setTimeout(() => setOpenMenuIdx(null), 120)
    }

    const handleNav = (href: string | null) => {
        if (!href) return
        setDrawerOpen(false)
        navigate(href)
    }

    // ── Early return ─────────────────────────────────────────────────────────
    if (!navBar) {
        return <AppBar position="fixed" sx={{ height: { xs: 56, lg: 64 }, bgcolor: '#030F26' }} />
    }

    const bgColor = navBar.theme?.themeStyles?.backgroundColor || '#030F26'

    return (
        <>
            {/* ════════════════════════════════════ AppBar ═══════════════════ */}
            <AppBar
                position="fixed"
                sx={[navBar.theme.themeStyles, { boxShadow: '0 2px 8px rgba(0,0,0,0.3)', zIndex: 1300 }]}
            >
                <Container maxWidth="xl" sx={{ height: '100%' }}>
                    <Toolbar
                        disableGutters
                        sx={{ height: { xs: 56, lg: 64 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}
                    >
                        {/* ── Logo ────────────────────────────────────────── */}
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, cursor: 'pointer' }}
                            onClick={() => navigate('/')}>
                            {getComponent('SECCION', navBar.components)?.map((sec: Component) =>
                                getComponent('LOGO', sec.components)?.map((logo: Component, i: number) => (
                                    <ComponentType key={i} component={logo}
                                        sx={{ height: { xs: 36, lg: 44 }, width: 'auto' }} />
                                ))
                            )}
                        </Box>

                        {/* ── Links Desktop ────────────────────────────────── */}
                        <Box sx={{
                            display: { xs: 'none', lg: 'flex' },
                            position: 'absolute', left: '10%',
                            alignItems: 'center', gap: 5,
                        }}>
                            {navGroups.map((group, idx) => {
                                const hasChildren = group.products.length > 0
                                const isOpen = openMenuIdx === idx

                                return (
                                    <Box
                                        key={idx}
                                        onMouseEnter={() => handleMouseEnter(idx)}
                                        onMouseLeave={handleMouseLeave}
                                        sx={{ position: 'relative', display: 'flex', alignItems: 'center', height: { lg: 64 } }}
                                    >
                                        {/* ── Item principal ─────────────── */}
                                        <Box
                                            onClick={() => handleNav(group.href)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 0.3,
                                                cursor: group.href ? 'pointer' : 'default',
                                                color: 'white',
                                                '&:hover': { opacity: isOpen ? 1 : 0.8 },
                                                transition: 'opacity 0.2s',
                                                userSelect: 'none',
                                                pb: isOpen ? '2px' : 0,
                                                borderBottom: isOpen ? '2px solid rgba(255,255,255,0.6)' : '2px solid transparent',
                                            }}
                                        >
                                            <Typography variant="body2" sx={{
                                                color: 'white', fontWeight: 500,
                                                fontSize: '0.95rem', letterSpacing: '0.02em',
                                            }}>
                                                {group.label}
                                            </Typography>
                                            {hasChildren && (
                                                <KeyboardArrowDownIcon sx={{
                                                    fontSize: '1rem', color: 'white',
                                                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s ease',
                                                }} />
                                            )}
                                        </Box>

                                        {/* ── Dropdown panel ─────────────── */}
                                        {hasChildren && isOpen && (
                                            <Box
                                                onMouseEnter={() => handleMouseEnter(idx)}
                                                onMouseLeave={handleMouseLeave}
                                                sx={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    mt: 0,
                                                    bgcolor: 'white',
                                                    borderRadius: '0 0 12px 12px',
                                                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                                                    minWidth: 200,
                                                    overflow: 'hidden',
                                                    zIndex: 1400,
                                                    animation: 'fadeSlideDown 0.15s ease',
                                                    '@keyframes fadeSlideDown': {
                                                        from: { opacity: 0, transform: 'translateX(-50%) translateY(-6px)' },
                                                        to:   { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
                                                    },
                                                }}
                                            >
                                                {/* Barra de color arriba del panel */}
                                                <Box sx={{ height: 3, bgcolor: '#db1d1f' }} />

                                                {/* Ir a la página del tipo (si tiene) */}
                                                {group.hasTypePage && (
                                                    <>
                                                        <Box
                                                            onClick={() => handleNav(group.href)}
                                                            sx={{
                                                                px: 2.5, py: 1.5, cursor: 'pointer',
                                                                display: 'flex', alignItems: 'center', gap: 1,
                                                                '&:hover': { bgcolor: '#f5f5f5' },
                                                            }}
                                                        >
                                                            <Typography variant="caption"
                                                                sx={{ color: '#db1d1f', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                Ver {group.label}
                                                            </Typography>
                                                        </Box>
                                                        <Divider />
                                                    </>
                                                )}

                                                {/* Productos del tipo */}
                                                {group.products.map((prod, pIdx) => (
                                                    <Box
                                                        key={pIdx}
                                                        onClick={() => handleNav(prod.href)}
                                                        sx={{
                                                            px: 2.5, py: 1.5,
                                                            cursor: 'pointer',
                                                            borderLeft: '3px solid transparent',
                                                            transition: 'all 0.15s',
                                                            '&:hover': {
                                                                bgcolor: '#fef2f2',
                                                                borderLeftColor: '#db1d1f',
                                                            },
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ color: '#001933', fontWeight: 500 }}>
                                                            {prod.displayName}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                )
                            })}
                        </Box>

                        {/* ── Espacio derecho desktop ──────────────────────── */}
                        <Box sx={{ display: { xs: 'none', lg: 'block' }, width: 80 }} />

                        {/* ── Hamburger ────────────────────────────────────── */}
                        <IconButton
                            onClick={() => setDrawerOpen(true)}
                            sx={{ color: 'white' }}
                            aria-label="Abrir menú"
                        >
                            <MenuIcon />
                        </IconButton>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* ══════════════════════════════════ Drawer mobile ═════════════ */}
            <Drawer
                anchor="top"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: bgColor,
                        color: 'white',
                        maxHeight: '85vh',
                        overflowY: 'auto',
                    },
                }}
            >
                {/* Cabecera del drawer */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 2, height: 56, borderBottom: '1px solid rgba(255,255,255,0.12)',
                }}>
                    <Box sx={{ cursor: 'pointer' }} onClick={() => handleNav('/')}>
                        {getComponent('SECCION', navBar.components)?.map((sec: Component) =>
                            getComponent('LOGO', sec.components)?.map((logo: Component, i: number) => (
                                <ComponentType key={i} component={logo} sx={{ height: 36, width: 'auto' }} />
                            ))
                        )}
                    </Box>
                    <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <List disablePadding>
                    {navGroups.map((group, idx) => {
                        const hasChildren = group.products.length > 0
                        const isExpanded  = mobileExpanded === idx

                        return (
                            <Box key={idx}>
                                {/* ── Item principal ───────────────── */}
                                <ListItemButton
                                    onClick={() => {
                                        if (hasChildren) {
                                            setMobileExpanded(isExpanded ? null : idx)
                                        } else {
                                            handleNav(group.href)
                                        }
                                    }}
                                    sx={{
                                        px: 3, py: 1.5,
                                        borderBottom: '1px solid rgba(255,255,255,0.07)',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                                    }}
                                >
                                    <ListItemText
                                        primary={group.label}
                                        primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem', color: 'white' }}
                                    />
                                    {hasChildren
                                        ? isExpanded
                                            ? <ExpandLessIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                                            : <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                                        : null
                                    }
                                </ListItemButton>

                                {/* ── Submenu accordion ─────────────── */}
                                {hasChildren && (
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        {/* "Ver {tipo}" si tiene página propia */}
                                        {group.hasTypePage && (
                                            <ListItem disablePadding>
                                                <ListItemButton
                                                    onClick={() => handleNav(group.href)}
                                                    sx={{ pl: 5, py: 1, bgcolor: 'rgba(219,29,31,0.15)', '&:hover': { bgcolor: 'rgba(219,29,31,0.25)' } }}
                                                >
                                                    <ListItemText
                                                        primary={`Ver ${group.label}`}
                                                        primaryTypographyProps={{ fontSize: '0.85rem', color: '#ff6b6b', fontWeight: 700 }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        )}

                                        {/* Productos */}
                                        {group.products.map((prod, pIdx) => (
                                            <ListItem key={pIdx} disablePadding>
                                                <ListItemButton
                                                    onClick={() => handleNav(prod.href)}
                                                    sx={{
                                                        pl: 5, py: 1,
                                                        bgcolor: 'rgba(0,0,0,0.2)',
                                                        borderLeft: '3px solid #db1d1f',
                                                        '&:hover': { bgcolor: 'rgba(219,29,31,0.12)' },
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={prod.displayName}
                                                        primaryTypographyProps={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </Collapse>
                                )}
                            </Box>
                        )
                    })}
                </List>
            </Drawer>
        </>
    )
}
